import { type Dispatch, type FC, type SetStateAction, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Save, Trash2Icon } from 'lucide-react';
import { capitalize, isEqual, kebabCase } from 'lodash';
import { zodResolver } from '@hookform/resolvers/zod';
import { ulid } from 'ulid';
import { z } from 'zod';
import { renderField } from '@/components/inspector/entity-fields/form/field-components';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { OptionItem } from '@/components/inspector/entity-fields/form/option-item';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { SHOW_ALLOW_NEW_OPTIONS } from '@/lib/metadataFieldFlags';
import { updateMetadata } from '@/services/metadata.service';
import { useMetadata } from '@/hooks/metadata/useMetadata';
import { typeOptions } from '@/pages/manage/metadata';
import { type MetadataField } from '@/types/metadata';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { FormLabel } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QueryKey } from '@/lib/queryKeys';
import { log } from '@/utilities/log';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export interface OptionType {
  name: string;
}

export type OptionItemProps = {
  options: Record<string, OptionType>;
  optionKey: string;
  editMode: string | null;
  setOptions: Dispatch<SetStateAction<Record<string, OptionType>>>;
  handleRemoveOption: (optionKey: string) => void;
  setEditMode: (value: SetStateAction<string | null>) => void;
  sortable?: boolean;
};

export const MetadataOptionItem: FC<OptionItemProps> = ({
  options,
  optionKey,
  editMode,
  setOptions,
  handleRemoveOption,
  setEditMode,
}) => {
  const tCommon = useTranslations('common');

  return (
    <OptionItem optionKey={optionKey} className="h-12">
      <div className="flex w-full items-center gap-2">
        <div className="flex grow items-center gap-2">
          {editMode === optionKey ? (
            // Pre-populated input field in edit mode
            <Input
              type="text"
              value={options[optionKey].name}
              onChange={(e) =>
                setOptions((prev) => ({
                  ...prev,
                  [optionKey]: { ...prev[optionKey], name: e.target.value },
                }))
              }
              className="h-9 bg-input-background"
              autoFocus
            />
          ) : (
            // Display as text if not in edit mode
            <span title={options[optionKey].name} className="line-clamp-1 flex-1 text-left text-xs break-all">
              {options[optionKey].name}
            </span>
          )}
        </div>

        <div className="flex gap-1">
          {editMode === optionKey ? (
            <OptionAction
              onClick={() => {
                // Check if the option is empty; if so, remove it
                if (!options[optionKey].name.trim()) {
                  handleRemoveOption(optionKey);
                }
                setEditMode(null); // Exit edit mode
              }}
              icon={Save}
              data-cy="option-action-button-metadata-save"
              tooltip={tCommon('save')}
            />
          ) : (
            <OptionAction
              onClick={() => setEditMode(optionKey)}
              icon={Pencil}
              type="button"
              data-cy="option-action-button-metadata-edit"
              tooltip={tCommon('edit')}
            />
          )}
          <OptionAction
            onClick={() => handleRemoveOption(optionKey)}
            icon={Trash2Icon}
            data-cy="option-action-button-metadata-delete"
            variant="destructive"
            tooltip={tCommon('delete')}
          />
        </div>
      </div>
    </OptionItem>
  );
};

const formatChoices = (options: Record<string, OptionType>) =>
  Object.fromEntries(
    Object.values(options)
      .filter((option) => option.name.trim() !== '')
      .map((option) => [kebabCase(option.name), option.name]),
  );

export const MetadataFields: FC<{ metadataId: string }> = ({ metadataId }) => {
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tMetadataFields = useTranslations('inspector.metadataFields');
  const tValidation = useTranslations('validation');
  const tToasts = useTranslations('toasts');

  const { metadata: fieldData, metadataIsFetching, metadataIsPending } = useMetadata(metadataId);
  const { toast } = useToast();

  const [editMode, setEditMode] = useState<string | null>(null); // Track the option being edited
  const [options, setOptions] = useState<Record<string, OptionType>>(
    fieldData?.options?.choices
      ? Object.fromEntries(
          Object.entries(fieldData.options.choices).map(([key, value]) => [key, { name: value as string }]),
        )
      : {},
  );
  // const [order, setOrder] = useState<Array<string>>(() => Object.keys(options));

  // // Keep `order` in sync if options are reset
  // useEffect(() => {
  //   setOrder(Object.keys(options));
  // }, [options]);

  // Create a dynamic form schema based on the fieldData
  const createFormSchema = (data: MetadataField) => {
    const schema: Record<string, z.ZodType<any>> = {};

    Object.keys(data).forEach((key) => {
      switch (key) {
        case 'name':
          schema[key] = z
            .string()
            .min(1, { message: tValidation('fieldRequired', { field: `${capitalize(key)} field` }) })
            .max(255, { message: tValidation('fieldMaxLength', { field: `${capitalize(key)} field`, max: 255 }) });
          break;
        case 'description':
          schema[key] = z.string().optional();
          break;
        case 'type':
          schema[key] = z.enum(['string', 'boolean', 'float', 'date', 'text', 'select', 'multi_select', 'datetime'], {
            error: (issue) => {
              if (!issue.input) {
                return tValidation('fieldRequired', { field: tValidation('fields.type') });
              }
            },
          });
          break;
        case 'options':
          schema[key] = z
            .union([
              z.array(z.any()),
              z.object({
                choices: z.record(z.string(), z.any()),
              }),
            ])
            .optional();
          break;
        case 'facet':
        case 'searchable':
        case 'public':
        case 'allowNewOptions':
          schema[key] = z.boolean();
          break;
        case 'position':
          schema[key] = z.coerce.number().min(0, { message: tValidation('positionMustBePositiveNumber') });
          break;
        default:
          break;
      }
    });

    return z.object(schema);
  };

  const formSchema = fieldData ? createFormSchema(fieldData) : z.object({});

  // Dynamic default values based on the fieldData
  const createDefaultValues = (data: MetadataField) => {
    const defaultValues: Partial<Record<keyof MetadataField, any>> = {};

    if (data) {
      Object.keys(data).forEach((key) => {
        defaultValues[key as keyof MetadataField] = data[key as keyof MetadataField];
      });

      return defaultValues;
    }
  };

  const form = useForm({
    resolver: zodResolver((fieldData && createFormSchema(fieldData)) ?? z.object({})),
    defaultValues: fieldData && createDefaultValues(fieldData),
  });

  const { watch } = form;
  const { handleError } = useFormErrorHandler(form.setError, form.getValues);

  // Watch for changes to the 'type' field and add options UI if the field is a 'select' or 'multi_select'
  const fieldType = watch('type');
  const isSelectType = fieldType === 'multi_select' || fieldType === 'select';

  // The choices editor lives in local state, outside react-hook-form, so isDirty
  // never reflects an option add/rename/delete.
  const optionsDirty = isSelectType && !isEqual(formatChoices(options), fieldData?.options?.choices ?? {});

  const handleRemoveOption = (key: string) => {
    setOptions((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleAddNewOption = () => {
    const newKey = ulid();
    setOptions((prev) => ({
      ...prev,
      [newKey]: { name: '' },
    }));
    setEditMode(newKey);
  };

  useEffect(() => {
    form.reset(createDefaultValues(fieldData));
    setOptions(
      fieldData?.options?.choices
        ? Object.fromEntries(
            Object.entries(fieldData.options.choices).map(([key, value]) => [key, { name: value as string }]),
          )
        : {},
    );
  }, [fieldData, form.reset]);

  const updateMetadataWithAuth = useAuthenticatedQueryFn(updateMetadata);

  const updateMetadataMutation = useMutation({
    mutationFn: updateMetadataWithAuth,
    onSuccess: async (_data, variables) => {
      toast({
        title: tToasts('updateMetadataMutation.title'),
        description: tToasts('updateMetadataMutation.description'),
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.MetadataList] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.Metadata, variables.id] }),
      ]);
    },
    onError: (err: BackendError) => {
      handleError(err);
      log(err);
    },
  });

  function onSubmit(values: z.infer<ReturnType<typeof createFormSchema>>) {
    // Collect all kebab-cased keys of non-empty options to check for duplicates
    const keys = Object.values(options)
      .filter((option) => option.name.trim() !== '')
      .map((option) => kebabCase(option.name));

    // Check for duplicate keys
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
    if (duplicates.length > 0) {
      toast({
        title: tToasts('duplicateOptionsFound.title'),
        description: tToasts('duplicateOptionsFound.description'),
      });
      return; // Prevent form submission
    }

    const choices = formatChoices(options);
    const formattedOptions = Object.keys(choices).length ? { choices } : {};

    updateMetadataMutation.mutate({
      id: fieldData.id,
      metadata: {
        ...values,
        options: formattedOptions,
      },
    });
  }

  const isLoading = updateMetadataMutation.isPending;

  return metadataIsPending ? (
    <div className="flex flex-col space-y-6 p-4">
      {Array.from(Array(3)).map((_, index) => (
        <div key={`skeleton-${index}`} className="flex flex-col space-y-3">
          <Skeleton className={cn('h-5 w-1/3', { 'w-2/5': index === 0 })} />
          <Skeleton className="h-8 w-full" />
          <Skeleton className={cn('h-5 w-1/3', { 'w-2/3': index === 1 })} />
        </div>
      ))}
    </div>
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex flex-col space-y-6 p-4">
        {Object.keys(fieldData).map((key) => {
          const validation = (formSchema.shape as Record<string, z.ZodType<any>>)[key];

          switch (key) {
            case 'name':
              return renderField(form, {
                'name': capitalize(key),
                'type': 'string',
                'slug': key,
                'description': key === 'name' ? tMetadataFields('nameDescription') : undefined,
                'disabled': metadataIsFetching || isLoading,
                validation,
                'data-cy': 'metadata-name-field',
                'isLoading': metadataIsFetching,
              });
            case 'description':
              return renderField(form, {
                'name': capitalize(key),
                'type': 'text',
                'slug': key,
                'description': tMetadataFields('descriptionDescription'),
                'disabled': metadataIsFetching || isLoading,
                validation,
                'data-cy': 'metadata-description-field',
                'isLoading': metadataIsFetching,
              });
            case 'type':
              return (
                <div key={key} className="flex flex-col gap-4">
                  {renderField(form, {
                    'name': capitalize(key),
                    'type': 'select',
                    'description': tMetadataFields('typeDescription'),
                    'slug': key,
                    'options': typeOptions,
                    'disabled': true,
                    validation,
                    'data-cy': 'metadata-type-field',
                    'isLoading': metadataIsFetching,
                    'value': fieldData[key],
                  })}
                  {/* Render options fields immediately after type field if fieldType is multi_select or select */}
                  {(fieldType === 'multi_select' || fieldType === 'select') && fieldData.options && (
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-start gap-2">
                        <FormLabel className="leading-4">
                          {fieldType === 'multi_select'
                            ? tMetadataFields('multiSelectOptions')
                            : tMetadataFields('selectOptions')}
                        </FormLabel>
                      </div>
                      <div className="relative">
                        <Options
                          handleAddNewOption={handleAddNewOption}
                          disabled={metadataIsFetching || isLoading}
                          // sortable
                          // order={order}
                          // onOrderChange={setOrder}
                        >
                          {Object.keys(options).map((optionKey) => (
                            <MetadataOptionItem
                              key={optionKey}
                              optionKey={optionKey}
                              options={options}
                              editMode={editMode}
                              setOptions={setOptions}
                              handleRemoveOption={handleRemoveOption}
                              setEditMode={setEditMode}
                            />
                          ))}
                        </Options>
                      </div>
                    </div>
                  )}
                </div>
              );
            case 'facet':
            case 'searchable':
            case 'public':
              return renderField(form, {
                'name': capitalize(key),
                'type': 'boolean',
                'slug': key,
                'description':
                  key === 'facet'
                    ? tMetadataFields('facetDescription')
                    : key === 'searchable'
                      ? tMetadataFields('searchableDescription')
                      : key === 'public'
                        ? tMetadataFields('publicDescription')
                        : undefined,
                'disabled': metadataIsFetching || isLoading,
                validation,
                'data-cy': `metadata-${key}-field`,
                'isLoading': metadataIsFetching,
              });
            case 'allowNewOptions':
              if (!SHOW_ALLOW_NEW_OPTIONS || !isSelectType) {
                return null;
              }
              return renderField(form, {
                'name': tMetadataFields('allowNewOptionsLabel'),
                'type': 'boolean',
                'slug': key,
                'description': tMetadataFields('allowNewOptionsDescription'),
                'disabled': metadataIsFetching || isLoading,
                validation,
                'data-cy': `metadata-${key}-field`,
                'isLoading': metadataIsFetching,
              });
            case 'position':
              return renderField(form, {
                'name': capitalize(key),
                'type': 'float',
                'slug': key,
                'description': tMetadataFields('positionDescription'),
                'disabled': metadataIsFetching || isLoading,
                validation,
                'min': 0,
                'data-cy': 'metadata-position-field',
                'isLoading': metadataIsFetching,
              });
            default:
              return null;
          }
        })}

        <Button
          disabled={metadataIsFetching || isLoading || metadataIsPending || !(form.formState.isDirty || optionsDirty)}
          type="submit"
          variant="navigation-action-primary"
          className="w-full justify-center focus-visible:ring-offset-ui-panel"
          data-cy="metadata-save-button"
          iconClassName={cn('icon-4!', { 'animate-spin': isLoading })}
          icon={isLoading ? Loader2 : Save}
        >
          {isLoading ? tCommon('saving') : tCommon('save')}
        </Button>
      </form>
    </FormProvider>
  );
};
